import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { userAPI } from '../utils/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
import './Profile.css';

export const Profile = () => {
  const { i18n } = useTranslation();
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [addressSelected, setAddressSelected] = useState(false);
  const [friendsShippingHere, setFriendsShippingHere] = useState([]);
  const isGerman = i18n.language === 'de';
  
  const [errors, setErrors] = useState({
    first_name: '',
    last_name: '',
    mpg_username: '',
    phone_number: '',
  });

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    mpg_username: '',
    phone_number: '',
    sponsor_name: '',
    shipping_location: 'england',
    ship_to_friend_username: '',
    street: '',
    house_no: '',
    zip: '',
    city: '',
    country: '',
    address_additional: '',
  });

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        mpg_username: user.mpg_username || '',
        phone_number: user.phone_number || '',
        sponsor_name: user.sponsor_name || '',
        shipping_location: user.shipping_location || 'england',
        ship_to_friend_username: user.ship_to_friend_username || '',
        street: user.address?.street || '',
        house_no: user.address?.house_no || '',
        zip: user.address?.zip || '',
        city: user.address?.city || '',
        country: user.address?.country || '',
        address_additional: user.address?.address_additional || '',
      });
    }
    
    loadFriendsShippingHere();
  }, [user]);

  const loadFriendsShippingHere = async () => {
    try {
      const response = await userAPI.getFriendsShippingHere();
      setFriendsShippingHere(response.data);
    } catch (error) {
      console.error('Failed to load friends:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    validateField(name, value);
  };

  const validateField = (name, value) => {
    let error = '';
    
    if (name === 'first_name' || name === 'last_name') {
      if (!/^[a-zA-ZäöüÄÖÜß\s-]*$/.test(value)) {
        error = isGerman 
          ? 'Nur Buchstaben erlaubt (keine Zahlen oder Sonderzeichen)' 
          : 'Only letters allowed (no numbers or special characters)';
      }
    }
    
    if (name === 'mpg_username') {
      if (!/^[a-zA-Z0-9äöüÄÖÜß]*$/.test(value)) {
        error = isGerman
          ? 'Nur Buchstaben, Zahlen und Umlaute erlaubt (keine Leerzeichen oder Sonderzeichen)'
          : 'Only letters, numbers and umlauts allowed (no spaces or special characters)';
      }
    }
    
    if (name === 'phone_number') {
      if (!/^[0-9+\s()-]*$/.test(value)) {
        error = isGerman
          ? 'Nur Zahlen, +, Leerzeichen und Klammern erlaubt'
          : 'Only numbers, +, spaces and parentheses allowed';
      }
    }
    
    setErrors({ ...errors, [name]: error });
  };

  const handleAddressSelect = (address) => {
    setFormData({
      ...formData,
      street: address.street,
      house_no: address.house_no,
      zip: address.zip,
      city: address.city,
      country: address.country,
    });
    setAddressSelected(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    if (errors.first_name || errors.last_name || errors.mpg_username || errors.phone_number) {
      toast.error(isGerman ? 'Bitte korrigieren Sie die markierten Fehler.' : 'Please correct the marked errors.');
      return;
    }

    const addressChanged = 
      formData.street !== user.address?.street || 
      formData.house_no !== user.address?.house_no ||
      formData.zip !== user.address?.zip ||
      formData.city !== user.address?.city ||
      formData.country !== user.address?.country ||
      formData.address_additional !== user.address?.address_additional;

    if (addressChanged && !addressSelected && 
        (formData.street !== user.address?.street || 
         formData.house_no !== user.address?.house_no ||
         formData.zip !== user.address?.zip ||
         formData.city !== user.address?.city ||
         formData.country !== user.address?.country)) {
      toast.error(isGerman ? 'Bitte wählen Sie eine gültige Adresse' : 'Please select a valid address');
      return;
    }

    setLoading(true);

    try {
      const updateData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        mpg_username: formData.mpg_username,
        phone_number: formData.phone_number,
        sponsor_name: formData.sponsor_name,
        shipping_location: formData.shipping_location,
        ship_to_friend_username: formData.ship_to_friend_username || null,
      };

      if (addressChanged) {
        updateData.address = {
          street: formData.street,
          house_no: formData.house_no,
          zip: formData.zip,
          city: formData.city,
          country: formData.country,
          address_additional: formData.address_additional || null,
        };
      }

      const response = await userAPI.updateProfile(updateData);
      updateUser(response.data);
      toast.success(isGerman ? 'Profil erfolgreich aktualisiert' : 'Profile updated successfully');
      setEditMode(false);
      setAddressSelected(false);
      loadFriendsShippingHere();
    } catch (error) {
      console.error('Update error:', error);
      const message = error.response?.data?.detail || 'Update failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        mpg_username: user.mpg_username || '',
        phone_number: user.phone_number || '',
        sponsor_name: user.sponsor_name || '',
        shipping_location: user.shipping_location || 'england',
        ship_to_friend_username: user.ship_to_friend_username || '',
        street: user.address?.street || '',
        house_no: user.address?.house_no || '',
        zip: user.address?.zip || '',
        city: user.address?.city || '',
        country: user.address?.country || '',
        address_additional: user.address?.address_additional || '',
      });
    }
    setEditMode(false);
    setAddressSelected(false);
  };

  return (
    <div className="profile-page">
      <div className="profile-container">
        <div className="profile-header">
          <h1>{isGerman ? 'Profil' : 'Profile'}</h1>
          <div className="header-actions">
            <button onClick={() => navigate('/change-password')} className="action-button secondary">
              {isGerman ? 'Passwort ändern' : 'Change Password'}
            </button>
          </div>
        </div>

        <div className="profile-card">
          {!editMode ? (
            <>
              <h2 className="profile-name">{user?.first_name} {user?.last_name}</h2>
              
              <div className="profile-info-grid">
                <div className="info-row">
                  <span className="info-label">{isGerman ? 'E-Mail' : 'Email'}:</span>
                  <span className="info-value">{user?.email}</span>
                </div>
                
                <div className="info-row">
                  <span className="info-label">MPG Username:</span>
                  <span className="info-value highlight">{user?.mpg_username}</span>
                </div>

                <div className="info-row">
                  <span className="info-label">{isGerman ? 'Telefonnummer' : 'Phone Number'}:</span>
                  <span className="info-value">{user?.phone_number}</span>
                </div>
                
                <div className="info-row">
                  <span className="info-label">Sponsor:</span>
                  <span className="info-value">{user?.sponsor_name}</span>
                </div>
                
                <div className="info-row">
                  <span className="info-label">{isGerman ? 'Versandoption' : 'Shipping Option'}:</span>
                  <span className="info-value">{user?.shipping_location === 'england' ? '🇬🇧 England' : '🇵🇭 Philippinen'}</span>
                </div>
                
                <div className="info-row">
                  <span className="info-label">{isGerman ? 'Versand an Freund' : 'Ship to Friend'}:</span>
                  <span className="info-value">{user?.ship_to_friend_username || (isGerman ? 'Keine' : 'None')}</span>
                </div>
              </div>
            </>
          ) : (
            <div className="edit-form">
              <h3>{isGerman ? 'Profil bearbeiten' : 'Edit Profile'}</h3>
              
              <div className="form-row">
                <div className="form-group">
                  <label>{isGerman ? 'Vorname' : 'First Name'}</label>
                  <input 
                    type="text" 
                    name="first_name" 
                    value={formData.first_name} 
                    onChange={handleChange}
                    className={errors.first_name ? 'input-error' : ''}
                  />
                  {errors.first_name && <span className="error-message">{errors.first_name}</span>}
                </div>
                <div className="form-group">
                  <label>{isGerman ? 'Nachname' : 'Last Name'}</label>
                  <input 
                    type="text" 
                    name="last_name" 
                    value={formData.last_name} 
                    onChange={handleChange}
                    className={errors.last_name ? 'input-error' : ''}
                  />
                  {errors.last_name && <span className="error-message">{errors.last_name}</span>}
                </div>
              </div>
              
              <div className="form-group">
                <label>MPG Username</label>
                <input 
                  type="text" 
                  name="mpg_username" 
                  value={formData.mpg_username} 
                  onChange={handleChange}
                  className={errors.mpg_username ? 'input-error' : ''}
                />
                {errors.mpg_username && <span className="error-message">{errors.mpg_username}</span>}
              </div>

              <div className="form-group">
                <label>{isGerman ? 'Telefonnummer' : 'Phone Number'}</label>
                <input
                  type="tel"
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={handleChange}
                  placeholder="+49 123 456789"
                  className={errors.phone_number ? 'input-error' : ''}
                />
                {errors.phone_number && <span className="error-message">{errors.phone_number}</span>}
              </div>

              <div className="form-group">
                <label>Sponsor</label>
                <input type="text" name="sponsor_name" value={formData.sponsor_name} onChange={handleChange} />
              </div>

              <div className="form-group">
                <label>{isGerman ? 'Versandoption' : 'Shipping Option'}</label>
                <select name="shipping_location" value="england" disabled className="shipping-select">
                  <option value="england">🇬🇧 England</option>
                </select>
              </div>

              <div className="form-group">
                <label>{isGerman ? 'Versand an Freund (Optional)' : 'Ship to Friend (Optional)'}</label>
                <input
                  type="text"
                  name="ship_to_friend_username"
                  value={formData.ship_to_friend_username}
                  onChange={handleChange}
                  placeholder={isGerman ? 'MPG Username des Freundes' : "Friend's MPG Username"}
                />
                <small className="field-hint">
                  {isGerman ? 'Leer lassen für Versand an eigene Adresse' : 'Leave empty to ship to your own address'}
                </small>
              </div>

              <div className="shipping-info-display england">
                <h4>🇬🇧 {isGerman ? 'Versand aus England' : 'Shipping from England'}</h4>
                <ul>
                  <li>✓ {isGerman ? 'Alle MPG Produkte verfügbar' : 'All MPG products available'}</li>
                  <li>✓ {isGerman ? 'Standard Versandkosten' : 'Standard shipping costs'}</li>
                  <li>✓ {isGerman ? 'Maximales Paketgewicht: 2kg' : 'Maximum package weight: 2kg'}</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        <div className="profile-card">
          <div className="card-header">
            <h2>{isGerman ? 'Versandadresse' : 'Shipping Address'}</h2>
            {!editMode && (
              <button onClick={() => setEditMode(true)} className="action-button primary">
                {isGerman ? 'Bearbeiten' : 'Edit'}
              </button>
            )}
          </div>

          {!editMode ? (
            <div className="address-display">
              <p>{user?.address?.street} {user?.address?.house_no}</p>
              {user?.address?.address_additional && (
                <p className="address-additional-display">↳ {user.address.address_additional}</p>
              )}
              <p>{user?.address?.zip} {user?.address?.city}</p>
              <p>{user?.address?.country}</p>
            </div>
          ) : (
            <form onSubmit={handleUpdate} className="address-form">
              <div className="address-info-box">
                <p>{isGerman ? '🗺️ Neue Adresse eingeben' : '🗺️ Enter new address'}</p>
              </div>

              <AddressAutocomplete 
                onAddressSelect={handleAddressSelect}
                initialValue={`${user?.address?.street} ${user?.address?.house_no}, ${user?.address?.zip} ${user?.address?.city}`}
              />

              <div className="form-group">
                <label>{isGerman ? 'Adresszusatz (Optional)' : 'Additional Address Info (Optional)'}</label>
                <input
                  type="text"
                  name="address_additional"
                  value={formData.address_additional}
                  onChange={handleChange}
                  placeholder={isGerman ? 'Etage, Hinweis für Postbote...' : 'Floor, Note for postman...'}
                />
              </div>

              {addressSelected && (
                <div className="address-preview">
                  <h4>{isGerman ? 'Neue Adresse:' : 'New Address:'}</h4>
                  <p>{formData.street} {formData.house_no}</p>
                  {formData.address_additional && <p>↳ {formData.address_additional}</p>}
                  <p>{formData.zip} {formData.city}</p>
                  <p>{formData.country}</p>
                </div>
              )}

              <div className="form-actions">
                <button type="button" onClick={handleCancel} className="action-button secondary" disabled={loading}>
                  {isGerman ? 'Abbrechen' : 'Cancel'}
                </button>
                <button type="submit" className="action-button primary" disabled={loading}>
                  {loading ? (isGerman ? 'Lädt...' : 'Loading...') : (isGerman ? 'Speichern' : 'Save')}
                </button>
              </div>
            </form>
          )}
        </div>

        {friendsShippingHere && friendsShippingHere.length > 0 && (
          <div className="profile-card">
            <h2>{isGerman ? '📦 Freunde, die an meine Adresse schicken' : '📦 Friends Shipping to My Address'}</h2>
            <p className="section-subtitle">
              {isGerman 
                ? `${friendsShippingHere.length} ${friendsShippingHere.length === 1 ? 'Freund schickt' : 'Freunde schicken'} Pakete an Ihre Adresse`
                : `${friendsShippingHere.length} ${friendsShippingHere.length === 1 ? 'friend ships' : 'friends ship'} packages to your address`}
            </p>
            <div className="friends-list">
              {friendsShippingHere.map((friend) => (
                <div key={friend.id} style={{
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
                  padding: '1.75rem',
                  borderRadius: '1rem',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  border: '2px solid #B3E0E0',
                  borderLeft: '6px solid #005F6B',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  minWidth: '280px'
                }}>
                  <div style={{ paddingLeft: '0.5rem' }}>
                    <div style={{
                      fontSize: '1.375rem',
                      color: '#4A3428',
                      fontWeight: '700',
                      marginBottom: '0.75rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <span>👤</span>
                      <span>{friend.first_name} {friend.last_name}</span>
                    </div>
                    
                    <div style={{
                      fontSize: '1.125rem',
                      color: '#005F6B',
                      fontWeight: '700',
                      background: 'linear-gradient(135deg, #E0F2FE 0%, #BAE6FD 100%)',
                      padding: '0.625rem 1rem',
                      borderRadius: '0.5rem',
                      fontFamily: 'Roboto Mono, monospace',
                      border: '1px solid #7DD3C0',
                      display: 'inline-block',
                      marginBottom: '1rem'
                    }}>
                      @{friend.mpg_username}
                    </div>
                    
                    <div style={{
                      fontSize: '1rem',
                      color: '#6B7280',
                      fontWeight: '500',
                      marginTop: '1rem',
                      paddingTop: '1rem',
                      borderTop: '2px solid #E5E7EB',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <span style={{ fontSize: '1.125rem' }}>✉️</span>
                      <span>{friend.email}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
